/**
 * Theme plumbing (spec §3 + ui.js behaviour 1.5-1): `tik.theme` is read once at boot, applied to
 * `html.tik-dark` and re-written on every effect run; the `<meta name="theme-color">` follows the
 * body background whenever the `<html>` class list changes.
 */
import { applyTheme, readTheme } from '$lib/store';

export type Theme = 'dark' | 'light';

/** Stored choice, else the OS preference. */
export function initialTheme(): Theme {
  return readTheme();
}

/** Toggle `html.tik-dark` and persist (writes the key on every call, as the legacy effect did). */
export function setTheme(theme: Theme): void {
  applyTheme(theme);
}

export function currentTheme(): Theme {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('tik-dark') ? 'dark' : 'light';
}

function isTransparent(color: string): boolean {
  const c = color.trim().toLowerCase();
  return !c || c === 'transparent' || /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0\s*\)$/.test(c);
}

/** Copy the body background into the theme-color meta (skipped when transparent). */
export function syncThemeColor(): void {
  if (typeof document === 'undefined' || !document.body) return;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) return;
  let bg = '';
  try {
    bg = getComputedStyle(document.body).backgroundColor;
  } catch {
    return;
  }
  if (!isTransparent(bg)) meta.setAttribute('content', bg);
}

/**
 * Watch `<html class>` and keep the theme-color meta in sync. Returns the disconnect function.
 */
export function startThemeColorSync(): () => void {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => undefined;
  const run = () => syncThemeColor();
  const mo = new MutationObserver(run);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
  return () => mo.disconnect();
}

/** `#tik-skip` click → focus `#tik-main` (ui.js behaviour 1.5-1). */
export function wireSkipLink(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const skip = document.getElementById('tik-skip');
  if (!skip) return () => undefined;
  const onClick = (ev: Event) => {
    ev.preventDefault();
    const main = document.getElementById('tik-main');
    main?.focus();
  };
  skip.addEventListener('click', onClick);
  return () => skip.removeEventListener('click', onClick);
}
